import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { audioService } from "../services/audioService";
import Reveal from "../components/Reveal";
import ErrorState from "../components/ErrorState";
import NotifyButton from "../components/NotifyButton";
import useSEO from "../hooks/useSEO";

const PlayIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// Eases a number from 0 to target for stat count-up
const useCountUp = (target, duration = 900) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
};

// Decorative animated waveform for the featured episode panel
const Waveform = () => (
  <div
    className="absolute right-6 sm:right-10 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-[5px] h-20 opacity-25"
    aria-hidden="true"
  >
    {[...Array(16)].map((_, i) => (
      <span
        key={i}
        className="w-[3px] rounded-full bg-white animate-equalize origin-center"
        style={{
          height: `${28 + ((i * 23) % 44)}px`,
          animationDelay: `${(i % 5) * 0.22}s`,
          animationDuration: `${1 + (i % 4) * 0.25}s`,
        }}
      />
    ))}
  </div>
);

const SkeletonRow = () => (
  <div className="card p-5 flex items-center gap-5">
    <div className="skeleton w-14 h-14 rounded-xl shrink-0" />
    <div className="flex-1 space-y-2.5">
      <div className="skeleton h-4 w-2/3" />
      <div className="skeleton h-3 w-1/3" />
    </div>
    <div className="skeleton h-10 w-24 rounded-lg shrink-0 hidden sm:block" />
  </div>
);

// Compact calendar-style date tile parsed from "2 July 2026".
// Morphs into a play button when the row is hovered.
const DateTile = ({ dateString }) => {
  const parts = (dateString || "").split(" ");
  const day = parts[0] || "–";
  const month = (parts[1] || "").slice(0, 3);
  return (
    <div className="relative w-14 h-14 shrink-0">
      <div className="absolute inset-0 rounded-xl bg-blue-50 border border-blue-100 flex flex-col items-center justify-center transition-all duration-300 group-hover:opacity-0 group-hover:scale-90">
        <span className="text-lg font-bold text-ink-900 leading-none">{day}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 mt-0.5">
          {month}
        </span>
      </div>
      <div className="absolute inset-0 rounded-xl bg-blue-600 text-white flex items-center justify-center opacity-0 scale-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100">
        <PlayIcon className="w-5 h-5" />
      </div>
    </div>
  );
};

const FeaturedEpisode = ({ audio }) => (
  <Link
    to={`/audio/${audio.id}`}
    className="group block card overflow-hidden mb-8 animate-fade-up hover:shadow-lg hover:border-blue-200 hover:-translate-y-0.5 transition-all duration-300"
  >
    <div className="bg-ink-900 px-6 sm:px-8 py-7 sm:py-9 relative overflow-hidden">
      {/* Sheen sweep on hover */}
      <div className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 bg-white/[0.06] skew-x-[-20deg] transition-transform duration-1000 ease-out group-hover:translate-x-[400%]" />
      <Waveform />

      <div className="relative md:pr-32">
        <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-blue-300 mb-3">
          Latest episode
        </span>
        <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-snug mb-3 max-w-3xl">
          {audio.title}
        </h2>
        {audio.description && (
          <p className="text-slate-300 text-sm sm:text-[15px] leading-relaxed line-clamp-2 max-w-3xl mb-5">
            {audio.description}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="inline-flex items-center gap-2.5 bg-blue-600 group-hover:bg-blue-500 text-white text-sm font-semibold pl-4 pr-5 py-2.5 rounded-lg transition-all duration-200 group-hover:gap-3.5">
            <PlayIcon />
            Listen now
          </span>
          <span className="text-xs text-slate-400 font-medium">{audio.publishDate}</span>
          {audio.itemCount && (
            <span className="text-xs text-slate-400 font-medium">
              {audio.itemCount} announcements covered
            </span>
          )}
        </div>
      </div>
    </div>
  </Link>
);

const EpisodeRow = ({ audio }) => (
  <Link
    to={`/audio/${audio.id}`}
    className="group card p-4 sm:p-5 flex items-center gap-4 sm:gap-5 hover:shadow-md hover:border-blue-200 hover:-translate-y-0.5 transition-all duration-300"
  >
    <DateTile dateString={audio.publishDate} />

    <div className="flex-1 min-w-0">
      <h3 className="text-[15px] sm:text-base font-semibold text-ink-900 leading-snug line-clamp-2 group-hover:text-blue-700 transition-colors duration-200">
        {audio.title}
      </h3>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
        {audio.itemCount && <span>{audio.itemCount} announcements</span>}
        {audio.category && (
          <>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span>{audio.category}</span>
          </>
        )}
      </div>
    </div>

    <span className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-blue-600 border border-blue-200 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 px-4 py-2 rounded-lg transition-all duration-200 shrink-0">
      <PlayIcon className="w-3.5 h-3.5" />
      Listen
    </span>
    <span className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-50 text-blue-600 shrink-0">
      <PlayIcon className="w-3.5 h-3.5" />
    </span>
  </Link>
);

const AudioList = () => {
  const [audioList, setAudioList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 9;

  useEffect(() => {
    const fetchAudioList = async () => {
      try {
        setLoading(true);
        const result = await audioService.getAudioList(currentPage, itemsPerPage);
        setAudioList(result.data);
        setTotalPages(result.totalPages);
        setTotal(result.total);
      } catch (err) {
        setError("Failed to load audio content");
      } finally {
        setLoading(false);
      }
    };

    fetchAudioList();
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const animatedTotal = useCountUp(total);

  useSEO({
    description:
      "Daily AWS news as a podcast. Every AWS announcement condensed into a short audio briefing — scripted by Amazon Bedrock AI, voiced by Amazon Polly. New episodes every day.",
    path: "/",
    jsonLd:
      audioList.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "AWS Daily Updates — Latest Episodes",
            numberOfItems: audioList.length,
            itemListElement: audioList.map((a, i) => ({
              "@type": "ListItem",
              position: (currentPage - 1) * itemsPerPage + i + 1,
              name: a.title,
              url: `https://deeppatel0311.github.io/aws-podcast/audio/${a.id}`,
            })),
          }
        : null,
  });

  if (error) {
    return (
      <ErrorState
        eyebrow="Something went wrong"
        title="We couldn't load the episodes"
        message="There was a problem reaching the podcast service. Please check your connection and try again."
        primaryLabel="Go to home"
        onRetry={() => window.location.reload()}
      />
    );
  }

  // The first item on page 1 is featured; the rest render as rows
  const featured = currentPage === 1 && audioList.length > 0 ? audioList[0] : null;
  const rows = featured ? audioList.slice(1) : audioList;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      {/* Page intro */}
      <div className="mb-10 animate-fade-up">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-ink-900 mb-3">
          Your daily AWS briefing
        </h1>
        <p className="text-slate-500 text-base sm:text-lg leading-relaxed max-w-2xl">
          Every AWS announcement, condensed into a short audio episode. Scripted
          by Amazon Bedrock, voiced by Amazon Polly — new episodes every day.
        </p>
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <NotifyButton variant="hero" />
          {total > 0 && (
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide tabular-nums">
              {animatedTotal} episodes published
            </p>
          )}
        </div>
      </div>

      {/* Episodes */}
      {loading ? (
        <div className="space-y-3">
          <div className="card overflow-hidden mb-8">
            <div className="skeleton h-52 rounded-none" />
          </div>
          {[...Array(5)].map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : (
        <>
          {featured && <FeaturedEpisode audio={featured} />}

          {rows.length > 0 && (
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
                {currentPage === 1 ? "Previous episodes" : `Page ${currentPage}`}
              </h2>
            </div>
          )}

          <div className="space-y-3">
            {rows.map((audio, index) => (
              <Reveal key={audio.id} delay={Math.min(index, 6) * 50}>
                <EpisodeRow audio={audio} />
              </Reveal>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && audioList.length === 0 && (
        <div className="card text-center py-16 animate-fade-in">
          <h3 className="text-lg font-semibold text-ink-900 mb-2">No episodes yet</h3>
          <p className="text-sm text-slate-500 mb-6">Check back soon for fresh AWS updates.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors duration-200"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-10 flex-wrap">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
            className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {[...Array(totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-10 h-10 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  currentPage === page
                    ? "bg-ink-900 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
            className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {!loading && audioList.length > 0 && (
        <p className="text-center mt-5 text-xs text-slate-400">
          Showing {(currentPage - 1) * itemsPerPage + 1}–
          {Math.min(currentPage * itemsPerPage, total)} of {total} episodes
        </p>
      )}
    </div>
  );
};

export default AudioList;
